import Bar from "../../../charts/Bar/Bar";
import Area from "../../../charts/Area/Area";
import { MdQueryStats } from "react-icons/md";
import classNames from "../../../../utilities/ClassNames";
import GaugeBox from "../GaugeBox/GaugeBox";
import React, { useEffect, useState, useMemo } from "react";
import { EmptyComponent } from "processhub-components-lib";
import { UnityGaugeBarProps } from "./domain/types";

const GaugeBar: React.FC<UnityGaugeBarProps> = ({ tab, unityGaugeBarConfig, hooks, setChartTypeComp }) => {
  const { useData } = hooks;
  const { getTagData, getExpressionData, areaApiConfig } = unityGaugeBarConfig;
  const { pastDate } = useData();
  const data = tab?.configurations;
  const [allData, setAllData] = useState<any>(null);
  const [current, setCurrent] = useState(0);
  const [chartType, setChartType] = useState<"gauge" | "area">("gauge");

  // Collect tag & expression IDs dynamically
  const { tagsIds, expressionsIds, namesCollection } = useMemo(() => {
    const tags: string[] = [];
    const expressions: string[] = [];
    const names: string[] = [];

    if (data) {
      Object.entries(data).forEach(([_, item]: any) => {
        if (!item?.id) return;

        names.push(item?.name);
        if (item.type === "tags" && item.value) {
          tags.push(item.id);
        } else if (item.value?.id) {
          expressions.push(item.value.id);
        }
      });
    }

    return { tagsIds: tags, expressionsIds: expressions, namesCollection: names };
  }, [data]);

  // Fetch data periodically
  useEffect(() => {
    const fetchData = async () => {
      try {
        let response;
        if (tagsIds.length) {
          response = await getTagData(tagsIds, pastDate);
        } else if (expressionsIds.length) {
          response = await getExpressionData(expressionsIds, pastDate);
        }
        if (response?.status === 200) setAllData(response.data);
      } catch (err) {
        console.error("GaugeBar fetch failed:", err);
      }
    };

    fetchData();
    const intervalId = setInterval(fetchData, 15000);
    return () => clearInterval(intervalId);
  }, [tagsIds, expressionsIds, pastDate, getTagData, getExpressionData]);

  const toggleChart = () => {
    setChartType((prev) => {
      const newType = prev === "gauge" ? "area" : "gauge";
      if (setChartTypeComp) {
        setChartTypeComp(newType);
      }
      return newType;
    });
  };

  const renderGauge = () => {
    if (!allData) return null;
    const item = allData[current];
    if (item === null) {
      return (
        <div className="w-full h-full grid place-content-center">
          <h1>N/A</h1>
        </div>
      );
    }
    return <GaugeBox data={item} name={namesCollection[current]} />;
  };

  const renderArea = () => {
    if (!allData) return null;
    const item = allData[current];
    return (
      <div className="w-[90%]">
        <Area
          item={item}
          type={item.tagNameIdentifier ? "tag" : "expression"}
          areaType="small"
          sidebarType="small"
          renderType="lazyLoading"
          maintainAspectRatio={false}
          areaApiConfig={areaApiConfig}
        />
      </div>
    );
  };

  return data ? (
    <div className="flex flex-col w-full h-full justify-center items-center">
      {/* Main chart */}
      <div
        className={classNames(
          "h-[57.5%]",
          chartType === "area" ? "pt-0" : "",
          "flex relative w-full justify-center bg-black p-1 rounded-md mb-2"
        )}
      >
        <div
          onClick={toggleChart}
          className="cursor-pointer z-20 text-white p-1 rounded-md bg-green duration-300 absolute left-0 top-0"
        >
          <MdQueryStats size={20} />
        </div>
        {chartType === "gauge" ? renderGauge() : renderArea()}
      </div>

      {/* Bar grid */}
      {allData && (
        <div className="h-[41%] grid grid-cols-2 grid-rows-2 lg:gap-1 2xl:gap-3 w-full">
          {allData.map(
            (item: any, index: number) =>
              item && (
                <div
                  key={index}
                  className="h-full bg-black rounded-md 2xl:py-0 px-2 cursor-pointer flex items-center"
                  onClick={() => {
                    setCurrent(index);
                    setChartType("gauge");
                  }}
                >
                  <Bar meta={item} barType="smallest" name={namesCollection[index]} />
                </div>
              )
          )}
        </div>
      )}
    </div>
  ) : (
    <EmptyComponent />
  );
};

export default GaugeBar;
